/**
 * Report Category Enum
 * Defines the types of citizen reports that can be submitted
 */
export type ReportCategory = 
  | 'ANTI_EPAL'
  | 'PROJECT_DELAY'
  | 'GHOST_PROJECT'
  | 'SERVICE_FAILURE'
  | 'COMPLIANCE_GAP';

/**
 * Report Category Configuration
 * Defines which fields are required/visible for each category
 */
export interface CategoryFieldConfig {
  required: string[];
  visible: string[];
  label: string;
  description: string;
}

export const REPORT_CATEGORIES: Record<ReportCategory, CategoryFieldConfig> = {
  ANTI_EPAL: {
    required: ['politician', 'location', 'image', 'isGovernmentFunded', 'projectType', 'fundingSource', 'violationType'],
    visible: ['politician', 'location', 'description', 'image', 'isGovernmentFunded', 'projectType', 'projectName', 'fundingSource', 'violationType'],
    label: 'Anti-Epal Watch',
    description: 'Reports documenting observed promotional materials on government-funded projects.',
  },
  PROJECT_DELAY: {
    required: ['location', 'image', 'projectName'],
    visible: ['location', 'description', 'image', 'projectName', 'expectedTimeline'],
    label: 'Project Delay',
    description: 'Report government projects that are delayed beyond expected timelines',
  },
  GHOST_PROJECT: {
    required: ['location', 'image', 'projectName'],
    visible: ['location', 'description', 'image', 'projectName', 'expectedTimeline'],
    label: 'Ghost Project',
    description: 'Report projects that were announced but never started or completed',
  },
  SERVICE_FAILURE: {
    required: ['location', 'image'],
    visible: ['location', 'description', 'image', 'serviceType'],
    label: 'Service Failure',
    description: 'Report failures in government service delivery',
  },
  COMPLIANCE_GAP: {
    required: ['location', 'image'],
    visible: ['location', 'description', 'image', 'complianceType'],
    label: 'Compliance Gap',
    description: 'Report gaps in compliance with regulations or standards',
  },
};

/**
 * Get category label
 */
export function getCategoryLabel(category: ReportCategory | string | undefined): string {
  if (!category) return 'Uncategorized';
  return REPORT_CATEGORIES[category as ReportCategory]?.label || category;
}

/**
 * Get category description
 */
export function getCategoryDescription(category: ReportCategory | string | undefined): string {
  if (!category) return '';
  return REPORT_CATEGORIES[category as ReportCategory]?.description || '';
}
